import { h } from 'preact';

type Props = {
  size?: number;
  class?: string;
  strokeWidth?: number;
};

function baseProps(p: Props) {
  return {
    width: p.size ?? 20,
    height: p.size ?? 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: p.strokeWidth ?? 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    class: p.class,
    'aria-hidden': true,
    focusable: 'false',
  };
}

export function IconBack(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like arrow-left */}
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

export function IconClose(p: Props) {
  return (
    <svg {...baseProps(p)}>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

export function IconGear(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like "settings" */}
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.09a2 2 0 0 1-1-1.74v-.51a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function IconHeart(p: Props & { filled?: boolean }) {
  const bp = baseProps(p);
  return (
    <svg {...bp} fill={p.filled ? 'currentColor' : 'none'} stroke="currentColor">
      {/* Lucide-like heart */}
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export function IconSearch(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like search */}
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

export function IconReceipt(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like receipt */}
      <path d="M4 2v20l4-2 4 2 4-2 4 2V2H4z" />
      <path d="M8 10h8" />
      <path d="M8 14h8" />
      <path d="M8 6h8" />
    </svg>
  );
}

export function IconChat(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like message-circle */}
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function IconRefresh(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like refresh-cw */}
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  );
}

export function IconCopy(p: Props) {
  return (
    <svg {...baseProps(p)}>
      {/* Lucide-like copy */}
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export function IconStar(p: Props & { filled?: boolean }) {
  const bp = baseProps(p);
  return (
    <svg {...bp} fill={p.filled ? 'currentColor' : 'none'}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" />
    </svg>
  );
}

export function IconTelegram(p: Props) {
  const size = p.size ?? 20;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      class={p.class}
      aria-hidden={true}
      focusable="false"
    >
      <defs>
        <linearGradient id="telegramPlaneGrad" x1="6" y1="6" x2="18" y2="18" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="#ffffff" />
          <stop offset="55%" stop-color="#dff6ff" />
          <stop offset="100%" stop-color="#8fddff" />
        </linearGradient>
      </defs>
      <path
        d="M20.32 5.84 17.73 18.05c-.2.93-.75 1.16-1.52.72l-3.94-2.9-1.9 1.84c-.21.21-.39.38-.81.38l.28-4.03 7.34-6.63c.32-.28-.07-.44-.49-.19l-9.08 5.72-3.91-1.22c-.85-.27-.87-.85.17-1.26l15.29-5.89c.71-.27 1.33.17.96 1.95Z"
        fill="url(#telegramPlaneGrad)"
      />
      <path
        d="M10.16 14.06 16.72 8.13"
        stroke="rgba(132,223,255,0.9)"
        stroke-width="0.85"
        stroke-linecap="round"
        opacity="0.55"
      />
    </svg>
  );
}
